import { checkThreshold } from './registerUtils.ts';

export type SummaryAggregateMode = 'sum' | 'avg' | 'priorityAvg';

export type SummaryThresholdMeta = {
  engineeringMin?: number;
  engineeringMax?: number;
  alarmLow?: number;
  alarmLowLow?: number;
  alarmHigh?: number;
  alarmHighHigh?: number;
};

export type RegisterLike = {
  name?: string;
  dictionaryKey?: string;
  meta?: Record<string, unknown>;
};

export type SummaryThresholdSpec = Record<string, { keys: string[]; mode: SummaryAggregateMode }>;

const THRESHOLD_KEYS: Array<keyof SummaryThresholdMeta> = [
  'engineeringMin',
  'engineeringMax',
  'alarmLow',
  'alarmLowLow',
  'alarmHigh',
  'alarmHighHigh',
];

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function normalizeKey(value: unknown): string {
  return String(value || '').trim().toUpperCase();
}

export function computeAggregateFromMap(
  registers: Array<{ name?: string }>,
  valueMap: Record<string, number | null>,
  mode: Exclude<SummaryAggregateMode, 'priorityAvg'>,
): number | null {
  const nums = registers
    .map((r) => (r?.name ? valueMap[r.name] : null))
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));

  if (!nums.length) return null;
  if (mode === 'sum') return nums.reduce((a, b) => a + b, 0);
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function collectThresholdMeta(registers: RegisterLike[], mode: 'sum' | 'avg'): SummaryThresholdMeta | null {
  if (!registers.length) return null;

  const output: SummaryThresholdMeta = {};

  for (const thresholdKey of THRESHOLD_KEYS) {
    const vals = registers
      .map((r) => toNumber(r?.meta?.[thresholdKey]))
      .filter((v): v is number => v !== null);

    if (!vals.length) continue;

    const computed = mode === 'sum'
      ? vals.reduce((a, b) => a + b, 0)
      : (vals.reduce((a, b) => a + b, 0) / vals.length);

    output[thresholdKey] = Math.round(computed * 100) / 100;
  }

  return Object.keys(output).length ? output : null;
}

export function resolveFieldThresholdMeta(
  field: string,
  registers: RegisterLike[],
  spec: SummaryThresholdSpec,
): SummaryThresholdMeta | null {
  const fieldSpec = spec[field];
  if (!fieldSpec) return null;

  if (fieldSpec.mode === 'priorityAvg') {
    for (const key of fieldSpec.keys) {
      const matches = registers.filter((r) => normalizeKey(r?.dictionaryKey) === normalizeKey(key));
      const meta = collectThresholdMeta(matches, 'avg');
      if (meta) return meta;
    }
    return null;
  }

  const matchKeys = new Set(fieldSpec.keys.map((k) => normalizeKey(k)));
  const matches = registers.filter((r) => matchKeys.has(normalizeKey(r?.dictionaryKey)));
  return collectThresholdMeta(matches, fieldSpec.mode);
}

export function clearThresholdIndicator(el: HTMLElement): void {
  el.style.backgroundColor = '';
  el.style.color = '';
  el.title = '';
}

export function applySummaryThresholdToElement(
  el: HTMLElement,
  field: string,
  value: unknown,
  registers: RegisterLike[],
  spec: SummaryThresholdSpec,
): void {
  const numericValue = toNumber(value);
  if (numericValue === null) {
    clearThresholdIndicator(el);
    return;
  }

  const meta = resolveFieldThresholdMeta(field, registers, spec);
  if (!meta) {
    clearThresholdIndicator(el);
    return;
  }

  const severity = checkThreshold(numericValue, meta);
  el.style.color = '#ffffff';
  el.style.transition = 'background-color 0.4s ease';

  if (severity === 'critical') {
    el.style.backgroundColor = '#7f1d1d';
    el.title = 'Critical threshold breach';
  } else if (severity === 'high') {
    el.style.backgroundColor = '#d32f2f';
    el.title = 'Threshold warning';
  } else {
    el.style.backgroundColor = '#2e7d32';
    el.title = 'Within threshold';
  }
}

export function getSummaryThresholdSeverity(
  field: string,
  value: unknown,
  registers: RegisterLike[],
  spec: SummaryThresholdSpec,
): 'critical' | 'high' | null {
  const numericValue = toNumber(value);
  if (numericValue === null) return null;

  const meta = resolveFieldThresholdMeta(field, registers, spec);
  if (!meta) return null;

  return checkThreshold(numericValue, meta);
}
